<template>
  <div>
    <div>
      <template>
        <v-row :class="isGlobalSearch ? '' : 'och-topMargin--packInfoOffset'">
          <v-col class="ml-2"
            ><h2 class="font-weight-thin">{{ isGlobalSearch ? "Search" : "" }} Release Notes</h2></v-col
          >
          <v-col class="text-right">
            <v-btn
              v-if="showAutoAssignControls"
              class="mr-3"
              small
              color="primary"
              elevation="0"
              @click="openAutoAssignDialog"
              data-cy="auto-assignment-button"
            >
              Auto Assignment
            </v-btn>
            <v-btn
              class="mr-3"
              small
              color="primary"
              elevation="0"
              @click="navigateToPrint()"
              data-cy="releaseNotePrint"
              v-if="!isGlobalSearch && !loading"
            >
              <v-icon small class="mr-2">fas fa-print</v-icon>
              PRINT PREVIEW
            </v-btn>
          </v-col>
        </v-row>
      </template>
      <filter-bar
        :allowEditFilters="true"
        :isVisible="true"
        :propFilters="filters"
        :searchable-contexts="searchableContextList"
        @refreshList="refreshList"
        @updateFilters="updateFilters"
      ></filter-bar>
    </div>
    <batch-edit-test-plan-container
      :selectedItems="testPlanSelectionList"
      @refreshList="refreshList"
      data-test="batch-edit-container-test-plan"
      v-if="isTestPlanList"
    ></batch-edit-test-plan-container>

    <batch-edit-release-note-container
      :selectedItems="releaseNoteSelectionList"
      @refreshList="refreshList"
      data-test="batch-edit-container-release-note"
      v-if="isReleaseNoteList || isOchinAdminGlobal"
    ></batch-edit-release-note-container>
    <div class="pl-1 caption font-italic mt-n2" data-cy="searchAndSelectText" v-if="isTestPlanList">
      {{ searchCountText }} {{ testPlanSelectionCountText }}
    </div>
    <div class="pl-1 caption font-italic mt-n2" data-cy="searchAndSelectText" v-if="isReleaseNoteList">
      {{ searchCountText }} {{ releaseNoteSelectionCountText }}
    </div>
    <div class="pl-1 caption font-italic mt-n2" data-cy="searchAndSelectText" v-if="isGlobalSearch">
      {{ searchCountText }} {{ releaseNoteSelectionCountText }}
    </div>
    <v-card outlined>
      <v-card-text data-cy="searchCard" class="pb-0">
        <v-text-field
          class="my-2"
          data-cy="quickSearch"
          dense
          filled
          hide-details
          label="Quick search"
          outlined
          prepend-inner-icon="search"
          single-line
          v-if="!isGlobalSearch"
          v-model="quickSearchTerms"
        >
        </v-text-field>
        <template v-if="isOchinUser">
          <release-note-table
            :isGlobalSearch="isGlobalSearch"
            :loading="tableLoading"
            :options="options"
            :quickSearchTerms="quickSearchTerms"
            :searchReleaseNoteList="searchReleaseNoteList"
            @paginate="handlePagination"
            @updateOptions="updateOptions"
            @refreshList="refreshList"
          >
          </release-note-table>
        </template>
        <template v-else>
          <test-plan-table
            :isGlobalSearch="isGlobalSearch"
            :loading="tableLoading"
            :options="options"
            :quickSearchTerms="quickSearchTerms"
            :testPlanList="searchReleaseNoteList"
            @paginate="handlePagination"
            @updateOptions="updateOptions"
            @refreshList="refreshList"
          >
          </test-plan-table>
        </template>
      </v-card-text>
    </v-card>
  <v-dialog v-model="isAutoAssignDialogOpen" max-width="900">
    <v-card>
      <v-card-title class="d-flex flex-column align-start">
        <span class="headline font-weight-thin">Auto Assignment</span>
        <span class="subtitle-1">{{ packTitle }}</span>
      </v-card-title>
      <v-card-text>
        <v-btn
          color="primary"
          elevation="0"
          :loading="isRunningOnDemand"
          :disabled="isRunningOnDemand"
          @click="runOnDemand"
        >
          Run Auto Assignment
        </v-btn>
        <v-row v-if="showNoChangesInfo" no-gutters class="mt-4">
          <v-col cols="12" lg="6">
            <v-alert
              type="info"
              outlined
              dismissible
              data-cy="no-changes-info-alert"
              @input="showNoChangesInfo = false"
            >
              No reassignments were needed. Runs with no changes are not recorded in the run history.
            </v-alert>
          </v-col>
        </v-row>
        <div class="d-flex align-center justify-space-between mt-4 mb-3">
          <h3 class="font-weight-thin">Run History</h3>
          <v-btn text small color="primary" :to="{ name: 'adminAutoAssignment' }">Manage Rules</v-btn>
        </div>
        <v-data-table
          :headers="runHistoryHeaders"
          :items="runHistory"
          :loading="isLoadingRunHistory"
          item-key="logId"
          show-expand
          dense
          disable-pagination
          hide-default-footer
          no-data-text="No runs recorded for this pack"
          data-cy="auto-assignment-run-history-table"
        >
          <template v-slot:item.content.triggeredAt="{ item }">
            {{ formatDateTime(item.content.triggeredAt) }}
          </template>
          <template v-slot:item.content.mode="{ item }">
            {{ item.content.mode === "uat" ? "UAT" : "On Demand" }}
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4">
              <template v-if="item.content.changes">
                <table v-if="item.content.changes.length" class="run-history-changes-table">
                  <thead>
                    <tr>
                      <th scope="col" style="width: 55%">Release Note</th>
                      <th scope="col" style="width: 22.5%">From</th>
                      <th scope="col" style="width: 22.5%">To</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="change in item.content.changes" :key="change.releaseNoteId">
                      <td>
                        <router-link :to="{ name: 'releaseNoteView', params: { packId: parseInt(packId, 10), releaseNoteId: change.releaseNoteId } }">
                          {{ change.title ? `${change.title} (ID:${change.releaseNoteId})` : change.releaseNoteId }}
                        </router-link>
                      </td>
                      <td>{{ formatUsername(change.fromAssignee, userMap) }}</td>
                      <td>{{ formatUsername(change.toAssignee, userMap) }}</td>
                    </tr>
                  </tbody>
                </table>
                <span v-else class="grey--text text--darken-1">No changes were recorded for this run.</span>
              </template>
              <span v-else class="grey--text text--darken-1">
                Change details were not collected into run history for this run. Individual note changes are still
                available in each note's change history.
              </span>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions class="pa-4 justify-end">
        <v-btn outlined @click="isAutoAssignDialogOpen = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { cloneDeep, differenceBy, get } from "lodash";

import { formatUsername } from "@/lib/formatters";
import { DASHBOARD_FILTER_MAP } from "@/lib/dashboardFilterMap";
import BatchEditTestPlanContainer from "@/components/BatchEditTestPlanContainer";
import BatchEditReleaseNoteContainer from "@/components/BatchEditReleaseNoteContainer";
import FilterBar from "@/components/filterBar";
import ReleaseNoteTable from "@/components/releaseNoteTable";
import TestPlanTable from "@/components/testPlanTable";

export default {
  name: "releaseNoteListComponent",
  components: {
    BatchEditTestPlanContainer,
    BatchEditReleaseNoteContainer,
    FilterBar,
    ReleaseNoteTable,
    TestPlanTable,
  },
  computed: {
    filters() {
      return (
        get(
          this,
          `$store.state.session.user.userConfiguration.widgets.releaseNoteList.filters[${this.searchContext}]`
        ) || []
      );
    },
    filterOverrideListIsUndefined() {
      return this.userConfig.filterOverrideList === undefined;
    },
    isTestPlanList() {
      return this.$route.name === "testPlanList";
    },
    isReleaseNoteList() {
      return this.$route.name === "releaseNoteList";
    },
    isOchinAdminGlobal() {
      return this.isGlobalSearch && this.hasAdminOchin;
    },
    searchContext() {
      if (this.isGlobalSearch) {
        return "global";
      }
      return this.packId;
    },
    searchCountText() {
      if (typeof this.searchTotalCount !== "number") {
        return "";
      }
      return `${this.searchTotalCount} Result${this.searchTotalCount === 1 ? "" : "s"}`;
    },
    testPlanSelectionCountText() {
      if (!this.testPlanSelectionList.length) {
        return;
      }
      return `| ${this.testPlanSelectionList.length} Selected`;
    },

    releaseNoteSelectionCountText() {
      if (!this.releaseNoteSelectionList.length) {
        return;
      }
      return `| ${this.releaseNoteSelectionList.length} Selected`;
    },
    selectedTestPlansNotVisible() {
      let selectedTestPlansNotVisible = [];
      if (!this.testPlanSelectionList.length) return selectedTestPlansNotVisible;
      selectedTestPlansNotVisible = differenceBy(
        this.testPlanSelectionList,
        this.searchReleaseNoteList,
        "releaseNoteId"
      );
      return selectedTestPlansNotVisible;
    },
    selectedReleaseNotesNotVisible() {
      let selectedReleaseNotesNotVisible = [];
      if (!this.releaseNoteSelectionList.length) return selectedReleaseNotesNotVisible;
      selectedReleaseNotesNotVisible = differenceBy(this.releaseNoteSelectionList, this.searchReleaseNoteList, "id");
      return selectedReleaseNotesNotVisible;
    },
    isGlobalSearch() {
      return !this.packId;
    },
    packId() {
      return this.$route.params.packId;
    },
    paginationPage() {
      return parseInt(this.$route.query.page, 10) || 1;
    },
    rowsPerPage() {
      return (
        this.$store.state.session.user.userConfiguration.widgets.releaseNoteList.rowsPerPage ||
        this.options.itemsPerPage
      );
    },
    searchableContextList() {
      const defaultContextList = ["commonFields"];
      if (this.isGlobalSearch) {
        defaultContextList.push("globalSearchFields");
      }

      if (this.isOchinUser) {
        defaultContextList.push("ochinFields");
      } else {
        defaultContextList.push("memberFields");
      }
      return defaultContextList;
    },
    sortBy() {
      return this.$store.state.session.user.userConfiguration.widgets.releaseNoteList.sortBy || [];
    },
    sortDesc() {
      return this.$store.state.session.user.userConfiguration.widgets.releaseNoteList.sortDesc || [];
    },
    tableLoading() {
      return this.loading || (!this.isOchinUser && this.filterOverrideListIsUndefined);
    },
    userConfig() {
      return this.$store.state.session.user.userConfiguration;
    },
    showAutoAssignControls() {
      return (
        !this.isOchinUser &&
        !this.isGlobalSearch &&
        this.hasAdminOrganization
      );
    },
    ...mapGetters({
      hasAdminOchin: "session/hasAdminOchin",
      hasAdminOrganization: "session/hasAdminOrganization",
      runHistory: "autoAssignment/getRunHistory",
      userMap: "user/getUserMap",
    }),
    ...mapState({
      currentServiceArea: state => state.session.serviceArea,
      isOchinUser: state => state.session.user.isOchinUser,
      packTitle: state => state.pack.pack.title,
      searchReleaseNoteList: state => state.releaseNote.searchReleaseNoteList,
      searchTotalCount: state => state.releaseNote.searchTotalCount,
      testPlanSelectionList: state => state.releaseNote.testPlanBatchEditSelectionList,
      releaseNoteSelectionList: state => state.releaseNote.releaseNoteBatchEditSelectionList,
    }),
  },
  async created() {
    this.loading = true;
    // The rowsPerPage computed property is retrieved from the user config.
    this.options.itemsPerPage = this.rowsPerPage;
    this.options.page = this.paginationPage;
    this.options.sortBy = this.sortBy;
    this.options.sortDesc = this.sortDesc;

    try {
      await this.applyDashboardDrillThrough();
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Could not apply dashboard filters. Please return to the dashboard and click through again." });
    }

    try {
      await Promise.all([this.fetchServiceAreaAttributeList(), this.refreshList(), this.fetchOchinUserList()]);
    } catch (e) {
      console.error(e);
      this.$notifyError({
        message: "Could not load required resources for search functionality. Please reload the page to try again.",
      });
    }

    this.loading = false;
  },
  data() {
    return {
      formatUsername,
      loading: false,
      options: {
        descending: false,
        page: 1,
        itemsPerPage: 25,
      },
      quickSearchTerms: "",
      isRunningOnDemand: false,
      isAutoAssignDialogOpen: false,
      isLoadingRunHistory: false,
      showNoChangesInfo: false,
      runHistoryHeaders: [
        { text: "", value: "data-table-expand", sortable: false, width: "40px" },
        { text: "Date / Time", value: "content.triggeredAt", sortable: false, width: "170px" },
        { text: "Triggered By", value: "displayName", sortable: false, width: "160px" },
        { text: "Mode", value: "content.mode", sortable: false, width: "110px" },
        { text: "Changed", value: "content.totalChanged", sortable: false, width: "90px" },
        { text: "Errors", value: "content.totalErrors", sortable: false, width: "80px" },
      ],
    };
  },
  methods: {
    deselectHiddenTestPlans() {
      let testPlanList = cloneDeep(this.testPlanSelectionList);
      let filteredList = differenceBy(testPlanList, this.selectedTestPlansNotVisible, "releaseNoteId");
      this.updateTestPlanBatchEditSelectionList(filteredList);
    },
    deselectHiddenReleaseNotes() {
      let releaseNoteList = cloneDeep(this.releaseNoteSelectionList);
      let filteredList = differenceBy(releaseNoteList, this.selectedReleaseNotesNotVisible, "id");
      this.updateReleaseNoteBatchEditSelectionList(filteredList);
    },
    handlePagination(val) {
      this.updatePaginationQuery(val);
      this.updateRowsPerPageConfig(val);
      if (this.isGlobalSearch) {
        this.refreshList();
      }
    },
    navigateToPrint() {
      if (this.isOchinUser) {
        this.$router.push({
          name: "releaseNotePrint",
          params: { releaseNoteList: this.searchReleaseNoteList },
        });
      } else {
        this.$router.push({
          name: "testPlanPrint",
          params: { testPlanList: this.searchReleaseNoteList },
        });
      }
    },
    async refreshList() {
      this.loading = true;
      try {
        let offset = this.options.itemsPerPage * (this.options.page - 1);
        let rowCount = this.options.itemsPerPage;
        const { searchContext, sortBy, sortDesc } = this;

        if (!this.isGlobalSearch) {
          // Override offset and rowCount to insure that the search bar works available in the non-global context works.
          offset = 0;
          rowCount = 9999;
        }
        await this.getSearchReleaseNoteList({ offset, rowCount, sortBy, sortDesc, searchContext });
      } catch (e) {
        console.error(e);
        this.$notifyError("Failed to refresh table list.");
      }

      if (this.selectedTestPlansNotVisible.length && this.isTestPlanList) {
        this.deselectHiddenTestPlans();
      }

      if (this.selectedReleaseNotesNotVisible.length && this.isReleaseNoteList) {
        this.deselectHiddenReleaseNotes();
      }
      this.loading = false;
    },
    async applyDashboardDrillThrough() {
      const pending = this.$store.state.pack.pendingDrillThrough;
      if (pending === null || pending === undefined) return;

      const {
        dashboardFilterField,
        dashboardFilterValue,
        dashboardFilterValueLabel,
        dashboardFilterField2,
        dashboardFilterValue2,
        dashboardFilterValueLabel2,
        dashboardExcludeNonQualifying,
      } = pending;

      const buildDashboardDrillThroughFilter = (fieldKey, rawValue, rawValueLabel) => {
        if (!fieldKey || typeof rawValue === "undefined") return null;

        const fieldConfig = DASHBOARD_FILTER_MAP[fieldKey];
        if (!fieldConfig) return null;

        let filterValue;
        if (fieldConfig.dataType === "dynamicDropdown") {
          const parsedValue = parseInt(rawValue, 10);
          if (!Number.isFinite(parsedValue)) return null;
          filterValue = parsedValue;
        } else {
          filterValue = rawValue;
        }

        // null is only meaningful as a filter value for user fields (null = Unassigned).
        // For string fields the backend's "in" operator cannot match NULL rows, so skip
        // rather than sending a value that will fail the config save.
        if (filterValue === null && fieldConfig.dataType !== "user") return null;

        const valueLabel =
          rawValueLabel != null
            ? String(rawValueLabel)
            : filterValue === null
            ? "Unassigned"
            : String(rawValue);

        return {
          ...fieldConfig,
          value: [filterValue],
          valueLabel,
        };
      };

      const filters = [];

      const primaryFilter = buildDashboardDrillThroughFilter(
        dashboardFilterField,
        dashboardFilterValue,
        dashboardFilterValueLabel
      );
      if (primaryFilter) {
        filters.push(primaryFilter);
      }

      const secondaryFilter = buildDashboardDrillThroughFilter(
        dashboardFilterField2,
        dashboardFilterValue2,
        dashboardFilterValueLabel2
      );
      if (secondaryFilter) {
        filters.push(secondaryFilter);
      }

      // Bail out if a filter field was specified but could not be resolved (unknown key or NaN),
      // so we don't wipe the user's existing filters on a bad navigation.
      if (dashboardFilterField && !filters.length) {
        this.$store.commit("pack/clearPendingDrillThrough");
        return;
      }

      if (dashboardExcludeNonQualifying) {
        filters.push({
          attributeName: "operationalizeType",
          attributeNameLabel: "Operationalize Type",
          dataType: "string",
          operator: "notIn",
          operatorLabel: "!=",
          valueType: "array",
          value: ["FYI", "Good to Know", "Optional"],
          valueLabel: "FYI, Good to Know, Optional",
        });
      }

      // Update filters and clear both override checkboxes in one config write.
      const config = this.userConfig;
      config.widgets.releaseNoteList.filters[this.searchContext] = filters;
      config.filterOverrideList = [];
      await this.updateUserConfiguration(config);
      this.$store.commit("pack/clearPendingDrillThrough");

      // Reset page state.
      this.options.page = 1;
      this.$store.dispatch("releaseNote/updateReleaseNoteListPage", 1);
    },
    async updateFilters(filters) {
      const config = this.userConfig;
      config.widgets.releaseNoteList.filters[this.searchContext] = filters;
      await this.updateUserConfiguration(config);
      this.updatePaginationQuery({ page: 1 });

      this.refreshList();
    },
    updateOptions(val) {
      Object.assign(this.options, val);
    },
    updatePaginationQuery(val) {
      const queryParamPage = parseInt(get(this, "$route.query.page", null), 10);
      const query = Object.assign({}, this.$route.query, { page: val.page });

      // Guard against watcher firing mid-navigation when $route has already changed away from the list.
      if (queryParamPage !== query.page && (this.$route.name === "releaseNoteList" || this.$route.name === "testPlanList")) {
        this.$router.push({ query });
      }

      this.options.page = val.page;
      this.$store.dispatch("releaseNote/updateReleaseNoteListPage", val.page);
    },
    async updateRowsPerPageConfig(val) {
      // The user config saves to "rowsPerPage", which is what "itemsPerPage" was called in Vuetify v1.
      this.options.itemsPerPage = val.itemsPerPage;
      this.options.sortBy = val.sortBy;
      this.options.sortDesc = val.sortDesc;

      const widgets = this.userConfig.widgets;
      widgets.releaseNoteList.rowsPerPage = val.itemsPerPage;
      widgets.releaseNoteList.sortBy = val.sortBy;
      widgets.releaseNoteList.sortDesc = val.sortDesc;

      await this.updateUserConfiguration({
        widgets,
      });
    },
    updateTestPlanSelectionList(val) {
      this.testPlanSelectionList = val;
    },
    async runOnDemand() {
      this.isRunningOnDemand = true;
      this.showNoChangesInfo = false;
      try {
        const response = await this.$store.dispatch("autoAssignment/runOnDemand", parseInt(this.packId, 10));
        const { totalChanged, totalErrors } = response.data.result;
        if (totalChanged > 0 && totalErrors === 0) {
          this.$notifySuccess(`${totalChanged} assignment(s) updated`);
        } else if (totalChanged > 0 && totalErrors > 0) {
          this.$notifyWarn(`${totalChanged} assigned, ${totalErrors} could not be assigned`);
        } else if (totalErrors > 0) {
          this.$notifyError("Auto assignment encountered errors — no release notes were assigned");
        } else {
          this.showNoChangesInfo = true;
        }
      } catch (e) {
        this.$notifyError("An error occurred running auto assignment");
        return;
      } finally {
        this.isRunningOnDemand = false;
      }
      await this.refreshList();
      try {
        await this.fetchRunHistory(parseInt(this.packId, 10));
      } catch (e) {
        this.$notifyError("An error occurred fetching run history");
      }
    },
    async openAutoAssignDialog() {
      this.isAutoAssignDialogOpen = true;
      this.$store.commit("autoAssignment/setRunHistory", []);
      this.isLoadingRunHistory = true;
      try {
        await this.fetchRunHistory(parseInt(this.packId, 10));
      } catch (e) {
        this.$notifyError("An error occurred fetching run history");
      } finally {
        this.isLoadingRunHistory = false;
      }
    },
    formatDateTime(isoString) {
      if (!isoString) return "—";
      return new Date(isoString).toLocaleString();
    },
    ...mapActions({
      fetchServiceAreaAttributeList: "admin/serviceAreaAttribute/list",
      getSearchReleaseNoteList: "releaseNote/getSearchReleaseNoteList",
      updateUserConfiguration: "session/updateUserConfiguration",
      fetchOchinUserList: "user/loadOchinUsers",
      fetchRunHistory: "autoAssignment/fetchRunHistory",
    }),
    ...mapMutations({
      updateTestPlanBatchEditSelectionList: "releaseNote/setTestPlanBatchEditSelectionList",
      updateReleaseNoteBatchEditSelectionList: "releaseNote/setReleaseNoteBatchEditSelectionList",
    }),
  },

  watch: {
    paginationPage(val) {
      this.updatePaginationQuery({ page: parseInt(val, 10) });
    },
    searchContext() {
      this.refreshList();
    },
    packId(newVal) {
      this.$store.commit("autoAssignment/setRunHistory", []);
      if (newVal) {
        this.showNoChangesInfo = false;
      }
    },
  },
};
</script>

<style scoped>
.och-dataTable--fixedHeader .v-data-table-header th {
  white-space: nowrap !important;
}

::v-deep .v-data-table__expanded__content {
  box-shadow: none !important;
  background-color: #f5f5f5;
}

::v-deep [data-cy="auto-assignment-run-history-table"] tbody tr:hover:not(.v-data-table__expanded__content),
::v-deep [data-cy="auto-assignment-run-history-table"] tbody tr:has(+ .v-data-table__expanded__content) {
  background-color: #ffffbf !important;
}

::v-deep [data-cy="auto-assignment-run-history-table"] .v-data-table__expand-icon {
  color: #1976d2;
}

::v-deep [data-cy="auto-assignment-run-history-table"] .v-data-table__expand-icon:focus {
  outline: 2px solid #1976d2 !important;
  outline-offset: 2px !important;
}

.run-history-changes-table {
  table-layout: fixed;
  width: calc(100% - 48px);
  border-collapse: collapse;
  background: transparent;
  font-size: 13px;
  margin-left: 48px;
  border-left: 3px solid #1976d2;
}

.run-history-changes-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 4px 16px 4px 0;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}

.run-history-changes-table th:first-child,
.run-history-changes-table td:first-child {
  padding-left: 20px;
}

.run-history-changes-table td {
  padding: 6px 16px 6px 0;
  border-bottom: thin solid rgba(0, 0, 0, 0.08);
}

.run-history-changes-table tr:last-child td {
  border-bottom: none;
}

.run-history-changes-table tr:hover td {
  background-color: #ffffbf;
}
</style>
